/**
 * Loading spinner の theme-aware 表示 helper (PR-52 / #290)
 *
 * 背景: 旧実装はロゴ img に常時 `invert` を付与しており、dark mode では
 * 黒ロゴ→白で自然だが、light mode でも白抜きされ白背景に沈んでいた。
 *
 * 本 helper はロゴ class を「light = 素の色 (黒+ブランド赤) / dark = invert で
 * 白」に切り替える theme-aware class を返し、bare `invert` (= light でも
 * 白抜き) の使い回しを構造的に防ぐ。
 *
 * pure (副作用なし)。SVG asset / 業務ロジックは変更しない。
 */

/** spinner サイズ別の Tailwind class 設定 */
export interface SpinnerSizeConfig {
  /** 外側コンテナ */
  outer: string;
  /** ロゴ img */
  logo: string;
  /** ring viewBox の一辺 (px) */
  ring: number;
  /** ring stroke 幅 */
  stroke: number;
}

export const SPINNER_SIZE_CONFIG: Record<'sm' | 'md' | 'lg', SpinnerSizeConfig> = {
  sm: { outer: 'h-10 w-10', logo: 'h-4 w-4', ring: 40, stroke: 2 },
  md: { outer: 'h-20 w-20', logo: 'h-9 w-9', ring: 80, stroke: 2.5 },
  lg: { outer: 'h-28 w-28', logo: 'h-12 w-12', ring: 112, stroke: 3 },
};

/**
 * ロゴ img の theme-aware class を返す。
 *
 * - light mode: invert を付けない (素の黒+ブランド赤ロゴを light bg 上で表示)
 * - dark mode: `dark:invert` で白抜きにし dark bg 上で視認可能に
 *
 * bare `invert` (light でも白抜き) は使わない。
 *
 * @param logoSizeClass サイズ別ロゴ class (例: 'h-9 w-9')
 */
export function resolveSpinnerLogoClass(logoSizeClass: string): string {
  return `${logoSizeClass} dark:invert`.trim();
}

/**
 * ロゴ class が theme-aware invert を使っているか判定する。
 * - `dark:invert` を含む かつ
 * - bare `invert` (`dark:` 等の variant prefix なしの単独 invert) を含まない
 *
 * テストで「light mode でも白抜き」回帰を防ぐための構造担保 helper。
 */
export function spinnerLogoUsesThemeAwareInvert(logoClass: string): boolean {
  if (!logoClass.includes('dark:invert')) return false;
  // bare `invert` (前が行頭/空白、後が空白/行末) を検出 — variant 付きは除外
  const bareInvert = /(^|\s)invert(\s|$)/.test(logoClass);
  return !bareInvert;
}

/** 背景リング (静止) の theme-aware base color class */
export const SPINNER_RING_BASE_CLASS = 'text-slate-200 dark:text-slate-700';

/**
 * 回転リングの animation class。
 *
 * - 通常時: `animate-spin` (1s 回転)
 * - `prefers-reduced-motion: reduce` 時: 完全停止ではなく **低速回転 (2.5s)**
 *
 * 旧実装 (PR-52) は `motion-reduce:animate-none` で完全停止していたが、
 * spinner は「読み込み進行中」を伝える essential な表示であり、完全停止すると
 * freeze と区別がつかない (2026-06-12 調査: OS の動き軽減設定が有効な環境で
 * 「スピナーが回っていない」と報告された)。motion-reduce の趣旨 (過剰な動きの
 * 抑制) は animation-duration の延長で満たす。`animate-none` (完全停止) を
 * この class に再導入しないこと。
 */
export const SPINNER_RING_SPIN_CLASS = 'animate-spin motion-reduce:[animation-duration:2.5s]';

/**
 * 非 fullScreen 表示のラッパー class。
 *
 * - `h-full`: 親が確定高を持つ文脈 (route Suspense fallback / ページ直下 =
 *   AppLayout の main 直子) では親の高さいっぱいに広がり、スピナーを
 *   コンテンツエリアの視覚中心に配置する
 * - `min-h-[50vh]`: 親高さが auto で `h-full` (height: 100%) が解決できない
 *   文脈 (DataTable 等のインライン利用) の最低高さフォールバック (従来挙動)
 *
 * 背景: 旧 class は `min-h-[50vh]` 単独だったため、50vh の箱がコンテンツ
 * 上端に上詰めされ、スピナー中心が視覚中心より大きく上 (上端から約 25vh)
 * に表示されていた (ロゴ縦位置ずれ調査 C 案)。
 */
export const SPINNER_INLINE_WRAPPER_CLASS = 'flex h-full min-h-[50vh] items-center justify-center';

/**
 * fullScreen overlay の theme-aware 背景 class。
 * light = 明るいスクリム / dark = 暗いスクリム。spinner 本体は transparent。
 */
export const SPINNER_FULLSCREEN_OVERLAY_CLASS = 'bg-white/60 dark:bg-gray-900/50';
